import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDrawerContext } from "../../context/drawerContext";
import SubscriptionProductCard from "../Shop/SubscriptionProductCard";
import Button from "../Button";
import CloseIcon from "/public/images/icons/close.svg?react";
import InfoIcon from "/public/images/icons/infoIcon.svg?react";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { useUserContext } from "../../context/userContext";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function UpgradeAvailable() {
  const { user, logout, updateTokens, refreshSubscription } = useUserContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const [availableUpgrades, setAvailableUpgrades] = useState([]);

  useEffect(() => {
    fetchAvailableUpgrades();
  }, []);

  const fetchAvailableUpgrades = async () => {
    await Sentry.startSpan(
      {
        name: "Fetch Available Upgrades",
        op: "http.request",
      },
      async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Cart/SubscriptionProducts?userAccountId=${user.id}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );

          // Set HTTP response status in the span
          span.setAttribute("http.status_code", response.status);

          if (response.ok) {
            const data = await response.json();
            setAvailableUpgrades(data);
          } else {
            span.setStatus("log"); // Mark the span as logged
            throw new Error("Failed to fetch subscription");
          }
        } catch (error) {
          span.setStatus("error");
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, error);
        } finally {
          span.end(); // Ensure span ends properly
        }
      }
    );
  };

  const UpgradeSubscriptionDrawer = () => {
    setOpen(true);
    setDrawerContent(<UpgradeSubscription subscriptionPlans={availableUpgrades} />);
  };

  return (
    <>
      {availableUpgrades.length > 0 && (
        <div className="w-full">
          {user?.subscription?.Name === "Lite" ? (
            <Button
              variant="quaternary"
              className="w-full"
              onClick={async () => {
                const freshSubscription = await refreshSubscription();
                if (freshSubscription?.Name === 'Lite') {
                  window.open(freshSubscription?.LiteShopUrl || '/shop/my-participants', '_blank');
                }
              }}
            >
              Upgrade Subscription
            </Button>
          ) : (
            <Button variant="quaternary" className="w-full" onClick={UpgradeSubscriptionDrawer}>
              Upgrade Subscription
            </Button>
          )}
        </div>
      )}
    </>
  );
}

const UpgradeSubscription = ({ subscriptionPlans }) => {
  const { setOpen } = useDrawerContext();
  const navigate = useNavigate();
  const [activePlan, setActivePlan] = useState(null);
  const [activePlanDetails, setActivePlanDetails] = useState(null);

  const handlePlanClick = (index, plan) => {
    setActivePlan(index);
    setActivePlanDetails(plan);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between w-full">
        <br />
        <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          UPGRADE
        </h2>
        <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
      </div>
      <div className="mt-6 flex flex-col gap-[24px] justify-stretch">
        <h3 className="text-[24px]">Upgrade your Subscription</h3>
        <div className="flex flex-col gap-4 max-h-[275px] overflow-y-scroll no-scrollbar">
          {subscriptionPlans?.map((plan, index) => (
            <SubscriptionProductCard
              key={index}
              product={plan}
              isActive={activePlan === index}
              onClick={() => handlePlanClick(index, plan)}
            />
          ))}
        </div>
        <div className="flex flex-col gap-[24px]">
          <Button
            variant="primary"
            className={`w-full mt-4 ${activePlan === null ? 'opacity-[0.15]' : ''}`}
            disabled={activePlan === null}
            onClick={() => {
              navigate(`/shop/checkout?priceId=${subscriptionPlans[activePlan]?.StripePriceId}&price=${subscriptionPlans[activePlan]?.Price}&purchasedSubscriptionId=${activePlanDetails?.SubscriptionId}`);
              setOpen(false);
            }}
          >
            Confirm & Continue
          </Button>
          <Button
            variant="primary"
            status="nobg"
            className="w-full mt-4"
            onClick={() => {
              navigate(`/upgrade`);
            }}
          >
            <InfoIcon />
            View Plan Details
          </Button>
        </div>
      </div>
    </div>
  );
};